import React, { useEffect, useState, useRef, useCallback } from 'react';

const ActionToolbar = ({ actions, orbRef }) => {
  const [actionPositions, setActionPositions] = useState([]);
  const [floatingOffsets, setFloatingOffsets] = useState([]);
  const [cardColors, setCardColors] = useState([]);
  const containerRef = useRef(null);
  const animationRef = useRef(null);
  const actionRefs = useRef([]);
  const colorAnimationRef = useRef(null);

  // Orb color calculation functions (mirroring the shader logic)
  const rgb2yiq = (r, g, b) => {
    const y = r * 0.299 + g * 0.587 + b * 0.114;
    const i = r * 0.596 - g * 0.274 - b * 0.322;
    const q = r * 0.211 - g * 0.523 + b * 0.312;
    return [y, i, q];
  };

  const yiq2rgb = (y, i, q) => {
    const r = y + 0.956 * i + 0.621 * q;
    const g = y - 0.272 * i - 0.647 * q;
    const b = y - 1.106 * i + 1.703 * q;
    return [r, g, b];
  };

  const adjustHue = (color, hueDeg) => {
    const hueRad = hueDeg * Math.PI / 180.0;
    const [y, i, q] = rgb2yiq(color[0], color[1], color[2]);
    const cosA = Math.cos(hueRad);
    const sinA = Math.sin(hueRad);
    const newI = i * cosA - q * sinA;
    const newQ = i * sinA + q * cosA;
    return yiq2rgb(y, newI, newQ);
  };

  const calculateOrbColorAtAngle = useCallback((angle, time, hue = 0) => {
    // Base colors from the shader
    const baseColor1 = [0.611765, 0.262745, 0.996078]; // Purple
    const baseColor2 = [0.298039, 0.760784, 0.913725]; // Blue
    const baseColor3 = [0.062745, 0.078431, 0.600000]; // Dark blue

    // Apply hue adjustment
    const color1 = adjustHue(baseColor1, hue);
    const color2 = adjustHue(baseColor2, hue);
    const color3 = adjustHue(baseColor3, hue);

    // Calculate color mixing factor (mirroring shader logic)
    const cl = Math.cos(angle + time * 2.0) * 0.5 + 0.5;
    
    // Mix colors based on angle and time
    const mixedColor = [
      color1[0] * (1 - cl) + color2[0] * cl,
      color1[1] * (1 - cl) + color2[1] * cl,
      color1[2] * (1 - cl) + color2[2] * cl
    ];

    // Further blend with color3 (simplified approximation)
    const intensity = 0.7; // Approximation of the shader's lighting calculations
    const finalColor = [
      mixedColor[0] * intensity + color3[0] * (1 - intensity),
      mixedColor[1] * intensity + color3[1] * (1 - intensity),
      mixedColor[2] * intensity + color3[2] * (1 - intensity)
    ];

    // Clamp values and convert to CSS
    const clampedColor = finalColor.map(c => Math.max(0, Math.min(1, c)));
    return `rgb(${Math.round(clampedColor[0] * 255)}, ${Math.round(clampedColor[1] * 255)}, ${Math.round(clampedColor[2] * 255)})`;
  }, []);

  // Function to get CSS custom property values for dynamic sizing
  const getOrbDimensions = () => {
    // Get CSS custom properties from the orb complex container
    const orbComplexContainer = containerRef.current?.closest('.orb-complex-container');
    if (orbComplexContainer) {
      const computedStyle = window.getComputedStyle(orbComplexContainer);
      const orbSizeStr = computedStyle.getPropertyValue('--orb-size').trim();
      const actionRadiusStr = computedStyle.getPropertyValue('--action-radius').trim();
      
      // Parse pixel values (remove 'px' suffix)
      const orbSize = parseFloat(orbSizeStr.replace('px', ''));
      const actionRadius = parseFloat(actionRadiusStr.replace('px', ''));
      
      if (!isNaN(orbSize) && !isNaN(actionRadius)) {
        return {
          width: orbSize,
          height: orbSize,
          actionRadius: actionRadius
        };
      }
    }
    
    // Fallback to responsive defaults if CSS custom properties aren't available
    const isMobile = window.innerWidth <= 480;
    const isTablet = window.innerWidth <= 768;
    return {
      width: isMobile ? 75 : isTablet ? 85 : 100,
      height: isMobile ? 75 : isTablet ? 85 : 100,
      actionRadius: isMobile ? 100 : isTablet ? 120 : 140
    };
  };

  // Function to get actual pill dimensions
  const getActualPillDimensions = (index) => {
    const element = actionRefs.current[index];
    if (!element) {
      // Fallback to estimation if element not yet rendered
      const action = actions[index];
      if (action && action.label) {
        const charCount = action.label.length;
        return Math.max(80, Math.min(200, charCount * 8 + 60));
      }
      return 120;
    }
    
    const rect = element.getBoundingClientRect();
    return rect.width;
  };

  // Function to calculate circular positions around the orb
  const calculateCircularPositions = (numActions) => {
    if (numActions === 0) return [];
    
    // Get actual orb dimensions and action radius from CSS custom properties
    const orbDimensions = getOrbDimensions();
    const orbRadius = Math.min(orbDimensions.width, orbDimensions.height) / 2;
    const actionRadius = orbDimensions.actionRadius || 140;
    
    // 6 predefined positions in the specified order:
    // 1. Upper left, 2. Upper right, 3. Left, 4. Right, 5. Lower left, 6. Lower right
    const predefinedAngles = [
      204,  // Upper right (-24 degrees from 0°)
      -24,  // Upper left (24 degrees before 180°)
      180,    // Right (0 degrees)
      0,  // Left (180 degrees)
      156,  // Lower left (24 degrees after 180°)
      24    // Lower right (24 degrees from 0°)
    ];
    
    const positions = [];
    
    // Use the first numActions positions from our predefined set
    for (let i = 0; i < numActions && i < predefinedAngles.length; i++) {
      const angleInDegrees = predefinedAngles[i];
      const angleInRadians = (angleInDegrees * Math.PI) / 180;
      
      // Determine if this is a right-side or left-side position
      const isRightSide = Math.abs(angleInDegrees) <= 90; // Close to 0° (right side)
      
      // Get the actual rendered pill width
      const actualPillWidth = getActualPillDimensions(i);
      const pillHalfWidth = actualPillWidth / 2;
      
      // Use the action radius from CSS custom properties instead of calculating
      // This ensures consistency with the container sizing
      const positionRadius = actionRadius;
      
      positions.push({
        x: positionRadius * Math.cos(angleInRadians),
        y: positionRadius * Math.sin(angleInRadians),
        angle: angleInRadians
      });
    }
    
    return positions;
  };

  // Initialize floating offsets when actions change
  useEffect(() => {
    if (actions && actions.length > 0) {
      // Initialize floating offsets for each action with immediate targets
      const initialOffsets = actions.map(() => {
        const angle = Math.random() * Math.PI * 2;
        const distance = Math.random() * 3; // Start with appropriate movement
        return {
          x: 0,
          y: 0,
          targetX: Math.cos(angle) * distance,
          targetY: Math.sin(angle) * distance,
          speed: 0.01 + Math.random() * 0.01 // Slower floating movement
        };
      });
      setFloatingOffsets(initialOffsets);
    } else {
      setFloatingOffsets([]);
    }
  }, [actions]);

  // Start floating animation (separate effect to avoid restarting on actions change)
  useEffect(() => {
    if (floatingOffsets.length === 0) return;

    // Floating animation
    let lastTime = 0;
    
    const animate = (currentTime) => {
      if (currentTime - lastTime > 16) { // ~60fps
        
        setFloatingOffsets(prev => {
          if (prev.length === 0) return prev;
          
          return prev.map((offset, index) => {
            // Generate new target if we're close to current target
            // Use squared distance to avoid expensive sqrt calculation
            const deltaX = offset.x - offset.targetX;
            const deltaY = offset.y - offset.targetY;
            const squaredDistanceToTarget = deltaX * deltaX + deltaY * deltaY;
            
            let newTargetX = offset.targetX;
            let newTargetY = offset.targetY;
            
            // 0.5 squared = 0.25 for distance comparison
            if (squaredDistanceToTarget < 0.25 || Math.random() < 0.02) {
              // Generate new random target within 3px radius for slower movement
              const angle = Math.random() * Math.PI * 2;
              const distance = Math.random() * 3;
              newTargetX = Math.cos(angle) * distance;
              newTargetY = Math.sin(angle) * distance;
            }
            
            // Smoothly move towards target
            const newX = offset.x + (newTargetX - offset.x) * offset.speed;
            const newY = offset.y + (newTargetY - offset.y) * offset.speed;
            
            return {
              ...offset,
              x: newX,
              y: newY,
              targetX: newTargetX,
              targetY: newTargetY
            };
          });
        });
        lastTime = currentTime;
      }
      animationRef.current = requestAnimationFrame(animate);
    };
    
    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [floatingOffsets.length]);

  // Color synchronization animation
  useEffect(() => {
    if (actionPositions.length === 0) return;

    let colorLastTime = 0;
    let lastHue = null;
    let lastTime = null;
    
    // Store target colors and current interpolated colors
    let targetColors = [];
    let currentInterpolatedColors = [];
    
    const animateColors = (currentTime) => {
      const time = currentTime * 0.001; // Convert to seconds like the shader
      
      // Calculate new target colors at 5fps (every 200ms)
      if (currentTime - colorLastTime > 200) {
        // Get current orb values if orbRef is available
        let currentHue = 0;
        if (orbRef?.current?.getCurrentValues) {
          const orbValues = orbRef.current.getCurrentValues();
          currentHue = orbValues.hue || 0;
        }
        
        // Performance optimization: only recalculate if hue or time changed significantly
        const hueChanged = lastHue === null || Math.abs(currentHue - lastHue) > 0.5;
        const timeChanged = lastTime === null || Math.abs(time - lastTime) > 0.05;
        
        if (hueChanged || timeChanged) {
          // Calculate new target colors for each action position
          targetColors = actionPositions.map((position) => {
            if (!position) return 'rgb(147, 51, 234)'; // Fallback purple
            
            // Use the position's angle to determine color at that point on the orb
            const orbColor = calculateOrbColorAtAngle(position.angle, time, currentHue);
            return orbColor;
          });
          
          // Initialize interpolated colors if first time
          if (currentInterpolatedColors.length === 0) {
            currentInterpolatedColors = [...targetColors];
          }
          
          lastHue = currentHue;
          lastTime = time;
        }
        
        colorLastTime = currentTime;
      }
      
      // Smoothly interpolate to target colors every frame (~60fps)
      if (targetColors.length > 0 && currentInterpolatedColors.length > 0) {
        const interpolatedColors = currentInterpolatedColors.map((currentColor, index) => {
          const targetColor = targetColors[index];
          if (!targetColor || !currentColor) return currentColor || 'rgb(147, 51, 234)';
          
          // Parse RGB values for interpolation
          const currentRgb = currentColor.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
          const targetRgb = targetColor.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
          
          if (!currentRgb || !targetRgb) return currentColor;
          
          // Smooth interpolation factor (adjust for smoothness vs performance)
          const lerpFactor = 0.08; // Smooth transition over ~1 second
          
          const r = Math.round(parseInt(currentRgb[1]) + (parseInt(targetRgb[1]) - parseInt(currentRgb[1])) * lerpFactor);
          const g = Math.round(parseInt(currentRgb[2]) + (parseInt(targetRgb[2]) - parseInt(currentRgb[2])) * lerpFactor);
          const b = Math.round(parseInt(currentRgb[3]) + (parseInt(targetRgb[3]) - parseInt(currentRgb[3])) * lerpFactor);
          
          return `rgb(${r}, ${g}, ${b})`;
        });
        
        currentInterpolatedColors = interpolatedColors;
        setCardColors(interpolatedColors);
      }
      
      colorAnimationRef.current = requestAnimationFrame(animateColors);
    };
    
    colorAnimationRef.current = requestAnimationFrame(animateColors);

    return () => {
      if (colorAnimationRef.current) {
        cancelAnimationFrame(colorAnimationRef.current);
      }
    };
  }, [actionPositions, orbRef]);

  // Calculate 3D perspective transformation based on position relative to orb
  const get3DTransform = (position, floatingOffset = { x: 0, y: 0 }) => {
    if (!position) return '';
    
    // Calculate the angle from orb center to this position
    const angleToPosition = Math.atan2(position.y, position.x);
    
    // Very pronounced 3D effect for testing
    const maxRotation = 45; // Much larger rotation for testing
    
    // Calculate rotations to make the edge closest to orb appear nearer
    const rotationX = Math.sin(angleToPosition) * maxRotation;
    const rotationY = -Math.cos(angleToPosition) * maxRotation;
    
    // Add Z translation for more depth
    const translateZ = -20;
    
    
    return `perspective(400px) translate3d(0px, 0px, ${translateZ}px) rotateX(${rotationX}deg) rotateY(${rotationY}deg)`;
  };

  // Initialize action refs when actions change
  useEffect(() => {
    actionRefs.current = Array(actions?.length || 0).fill(null);
  }, [actions?.length]);

  // Calculate positions when actions change or window resizes
  useEffect(() => {
    const updatePositions = () => {
      if (actions && actions.length > 0) {
        // Longer delay to ensure DOM is fully rendered and styled
        setTimeout(() => {
          const positions = calculateCircularPositions(actions.length);
          setActionPositions(positions);
        }, 100);
      } else {
        setActionPositions([]);
      }
    };

    updatePositions();

    // Listen for window resize to recalculate positions
    const handleResize = () => {
      setTimeout(updatePositions, 150); // Longer delay on resize for DOM updates
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [actions]);

  if (!actions || actions.length === 0) {
    return null;
  }

  const handleRestart = () => {
    window.location.reload();
  };

  return (
    <div ref={containerRef} className="circular-action-container">
      {actions.map((action, index) => {
        const position = actionPositions[index];
        const floatingOffset = floatingOffsets[index] || { x: 0, y: 0 };
        if (!position) return null;

        // Calculate 3D effect using shadows and gradients instead of transforms
        const angleToOrb = Math.atan2(position.y, position.x);
        const shadowDirection = {
          x: Math.cos(angleToOrb + Math.PI) * 4, // Shadow away from orb
          y: Math.sin(angleToOrb + Math.PI) * 4
        };
        
        // Calculate lighting direction (opposite of shadow)
        const lightDirection = {
          x: -shadowDirection.x,
          y: -shadowDirection.y
        };
        
        // Get the color for this card based on its position relative to the orb
        const cardColor = cardColors[index] || 'rgb(147, 51, 234)'; // Fallback to purple
        
        // Parse RGB values from the calculated color
        const rgbMatch = cardColor.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
        const [r, g, b] = rgbMatch ? [parseInt(rgbMatch[1]), parseInt(rgbMatch[2]), parseInt(rgbMatch[3])] : [147, 51, 234];
        
        const style = {
          position: 'absolute',
          left: `calc(50% + ${position.x + floatingOffset.x}px)`,
          top: `calc(50% + ${position.y + floatingOffset.y}px)`,
          transform: `translate(-50%, -50%)`,
          zIndex: 10,
          transformStyle: 'preserve-3d',
          // 3D effect using dynamic orb colors
          boxShadow: `
            ${shadowDirection.x}px ${shadowDirection.y}px 10px rgba(0,0,0,0.3),
            ${shadowDirection.x * 0.5}px ${shadowDirection.y * 0.5}px 5px rgba(0,0,0,0.2),
            inset ${lightDirection.x * 0.6}px ${lightDirection.y * 0.6}px 4px rgba(${r}, ${g}, ${b}, 0.25),
            inset ${lightDirection.x * 0.3}px ${lightDirection.y * 0.3}px 2px rgba(${r}, ${g}, ${b}, 0.2),
            0 0 15px rgba(${r}, ${g}, ${b}, 0.4)
          `,
          // Dynamic gradient using orb colors
          background: `
            linear-gradient(${angleToOrb * 180 / Math.PI + 90}deg, 
              rgba(${r}, ${g}, ${b}, 0.15) 0%, 
              rgba(${r}, ${g}, ${b}, 0.12) 30%, 
              rgba(${Math.round(r * 0.6)}, ${Math.round(g * 0.6)}, ${Math.round(b * 0.6)}, 0.08) 70%, 
              rgba(0,0,0,0.06) 100%
            )
          `,
          // Dynamic border that matches the orb color
          border: `1px solid rgba(${r}, ${g}, ${b}, 0.3)`
        };
        

        if (action && action.type === 'link' && action.url) {
          return (
            <a
              key={index}
              ref={(el) => actionRefs.current[index] = el}
              className="action-chip circular-action"
              href={action.url}
              target="_blank"
              rel="noopener noreferrer"
              style={style}
            >
              {/* Favicon */}
              <img
                src={`https://www.google.com/s2/favicons?sz=16&domain=${(() => {
                  try {
                    return new URL(action.url).hostname;
                  } catch (e) {
                    return 'example.com';
                  }
                })()}`}
                alt=""
                referrerPolicy="no-referrer"
                loading="lazy"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
              
              {/* Label */}
              <span>{action.label || action.url}</span>
              
              {/* Link icon */}
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path fill="currentColor" d="M14 3h7v7h-2V6.41l-9.29 9.3-1.42-1.42 9.3-9.29H14V3zM5 5h7v2H7v10h10v-5h2v7H5V5z"/>
              </svg>
            </a>
          );
        } else if (action && action.type === 'restart') {
          return (
            <button
              key={index}
              ref={(el) => actionRefs.current[index] = el}
              className="action-chip circular-action restart-action"
              onClick={handleRestart}
              style={style}
            >
              {/* Restart icon */}
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path fill="currentColor" d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z"/>
              </svg>
              
              {/* Label */}
              <span>{action.label || 'Restart'}</span>
            </button>
          );
        }
        return null;
      })}
    </div>
  );
};

export default ActionToolbar;
